import { AlertType, AlertData, AlertConfig, DEFAULT_ALERT_CONFIG } from '../components/base/Alert';

type AlertCallback = (alerts: AlertData[]) => void;

/**
 * Глобальный менеджер алертов для управления уведомлениями в приложении
 *
 * @example
 * // Базовое использование
 * alertManager.success('Операция выполнена успешно!');
 * alertManager.error('Произошла ошибка');
 *
 * // С кастомной конфигурацией
 * alertManager.warning('Внимание!', { duration: 10000, autoHide: true });
 *
 * // С предустановленными конфигурациями
 * import { ALERT_CONFIGS } from '../constants/alerts';
 * alertManager.info('Информация', ALERT_CONFIGS.LONG);
 *
 * // Установка глобальной конфигурации по умолчанию
 * alertManager.setDefaultConfig({ duration: 5000, autoHide: true });
 */
class AlertManager {
    private static instance: AlertManager;
    private alerts: AlertData[] = [];
    private subscribers: AlertCallback[] = [];
    private nextId = 1;
    private defaultConfig: AlertConfig = DEFAULT_ALERT_CONFIG;

    private constructor() {}

    static getInstance(): AlertManager {
        if (!AlertManager.instance) {
            AlertManager.instance = new AlertManager();
        }
        return AlertManager.instance;
    }

    /**
     * Устанавливает глобальную конфигурацию по умолчанию для всех алертов
     * @param config - Частичная конфигурация для переопределения
     *
     * @example
     * alertManager.setDefaultConfig({ duration: 8000 });
     * alertManager.setDefaultConfig({ autoHide: false });
     */
    setDefaultConfig(config: Partial<AlertConfig>): void {
        this.defaultConfig = { ...this.defaultConfig, ...config };
    }

    /**
     * Возвращает текущую глобальную конфигурацию по умолчанию
     * @returns Текущая конфигурация
     */
    getDefaultConfig(): AlertConfig {
        return { ...this.defaultConfig };
    }

    /**
     * Подписывается на изменения алертов
     * @param callback - Функция обратного вызова
     * @returns Функция для отписки
     */
    subscribe(callback: AlertCallback): () => void {
        this.subscribers.push(callback);

        // Возвращаем функцию для отписки
        return () => {
            const index = this.subscribers.indexOf(callback);
            if (index > -1) {
                this.subscribers.splice(index, 1);
            }
        };
    }

    private notifySubscribers(): void {
        this.subscribers.forEach((callback) => callback([...this.alerts]));
    }

    /**
     * Показывает алерт с указанным типом и сообщением
     * @param type - Тип алерта
     * @param message - Сообщение алерта
     * @param config - Опциональная конфигурация для переопределения
     * @returns ID алерта
     *
     * @example
     * const alertId = alertManager.show('success', 'Успешно!', { duration: 1500 });
     *
     * // С предустановленной конфигурацией
     * import { ALERT_CONFIGS } from '../constants/alerts';
     * alertManager.show('error', 'Ошибка!', ALERT_CONFIGS.PERSISTENT);
     */
    show(type: AlertType, message: string, config?: Partial<AlertConfig>): string {
        const id = `alert-${this.nextId++}`;

        // Объединяем конфигурацию по умолчанию с переданной
        const finalConfig = { ...this.defaultConfig, ...config };

        const alert: AlertData = {
            type,
            message,
            id,
            config: finalConfig,
        };

        this.alerts.push(alert);
        this.notifySubscribers();

        if (finalConfig.autoHide) {
            setTimeout(() => {
                this.hide(id);
            }, finalConfig.duration);
        }

        return id;
    }

    /**
     * Скрывает алерт по ID или все алерты
     * @param id - ID алерта для скрытия (если не указан, скрывает все)
     *
     * @example
     * alertManager.hide('alert-1'); // Скрыть конкретный алерт
     * alertManager.hide(); // Скрыть все алерты
     */
    hide(id?: string): void {
        if (id) {
            this.alerts = this.alerts.filter((alert) => alert.id !== id);
        } else {
            this.alerts = [];
        }
        this.notifySubscribers();
    }

    /**
     * Возвращает все активные алерты
     * @returns Массив активных алертов
     */
    getAlerts(): AlertData[] {
        return [...this.alerts];
    }

    /**
     * Показывает алерт успеха
     * @param message - Сообщение
     * @param config - Опциональная конфигурация
     * @returns ID алерта
     *
     * @example
     * alertManager.success('Операция выполнена!');
     * alertManager.success('Сохранено!', { duration: 3000 });
     */
    success(message: string, config?: Partial<AlertConfig>): string {
        return this.show('success', message, config);
    }

    /**
     * Показывает алерт ошибки
     * @param message - Сообщение
     * @param config - Опциональная конфигурация
     * @returns ID алерта
     *
     * @example
     * alertManager.error('Произошла ошибка!');
     * alertManager.error('Ошибка сети!', { autoHide: false });
     */
    error(message: string, config?: Partial<AlertConfig>): string {
        return this.show('error', message, config);
    }

    /**
     * Показывает алерт предупреждения
     * @param message - Сообщение
     * @param config - Опциональная конфигурация
     * @returns ID алерта
     *
     * @example
     * alertManager.warning('Внимание!');
     * alertManager.warning('Проверьте данные!', { duration: 5000 });
     */
    warning(message: string, config?: Partial<AlertConfig>): string {
        return this.show('warning', message, config);
    }

    /**
     * Показывает информационный алерт
     * @param message - Сообщение
     * @param config - Опциональная конфигурация
     * @returns ID алерта
     *
     * @example
     * alertManager.info('Информация');
     * alertManager.info('Загрузка завершена', { duration: 1500 });
     */
    info(message: string, config?: Partial<AlertConfig>): string {
        return this.show('info', message, config);
    }
}

// Экспортируем синглтон
export const alertManager = AlertManager.getInstance();

// Экспортируем класс для тестирования
export { AlertManager };
